import React, { useState } from 'react'
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import Popup from 'reactjs-popup'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'

// components
import { Input } from '../../../Inputs/Input'
import { Sparkle } from '../../../Icons/Sparkle'
import { createBrandDetails } from '../../../../requests/brandDetails'
import { Empty } from '../../../Empty'

// context
import { useSavedBrandDetails } from '../../contexts/useSavedBrandDetails'

// utils
import { createBrandDetailsValidation } from '../../../../../server/validation/brandDetails'
import { handleError } from '../../../../errors/handleError'

// assets
const WebsiteIcon = '/assets/website-icon.png'
const IndustryIcon = '/assets/industry-icon.png'
const ToneIcon = '/assets/tone-icon.png'

import './styles.css'

type Props = {
  onBrandDetailsSelected: (brandDetailsId: string) => void
}

export const CustomizeWithBrandDetails: React.FC<Props> = ({
  onBrandDetailsSelected
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    savedBrandDetails,
    setSavedBrandDetails
  } = useSavedBrandDetails()

  const formik = useFormik({
    initialValues: {
      websiteUrl: '',
      industry: '',
      tone: ''
    },
    validationSchema: createBrandDetailsValidation,
    onSubmit: async (values) => {
      if (!values.industry && !values.tone && !values.websiteUrl) {
        toast.error('Please fill in at least one field')
        return
      }

      try {
        const res = await createBrandDetails(values)

        setSavedBrandDetails([...savedBrandDetails, res.data])

        formik.resetForm()
        setIsOpen(false)

        onBrandDetailsSelected(res.data._id)
      } catch (err) {
        handleError(err, 'Failed to create brand details. Please try again later.')
      }
    }
  })

  // Helper function to get hostname from URL
  const getHostname = (url: string): string => {
    try {
      return new URL(url).hostname
    } catch (_e) {
      return url
    }
  }

  return (
    <Popup
      open={isOpen}
      onClose={() => setIsOpen(false)}
      trigger={
        <button type="button" className="secondary" onClick={() => setIsOpen(true)}>
          <Sparkle />
          <span>Customize</span>
        </button>
      }
      closeOnEscape
      position="bottom right"
    >
      <div className="brand-details-container">
        <h3>Customize for a brand</h3>
        <Tabs>
          <TabList>
            <Tab>Saved Brands</Tab>
            <Tab>Add New</Tab>
          </TabList>

          <TabPanel>
            {
              savedBrandDetails.length === 0 && (
                <Empty>
                  No saved brand details yet. Add a new brand to get started.
                </Empty>
              )
            }
            {
              savedBrandDetails.length > 0 && (
                <div className="saved-brand-details-container">
                  {savedBrandDetails.map((brand) => (
                    <button
                      key={brand._id}
                      type="button"
                      className="saved-brand-details-card"
                      onClick={() => onBrandDetailsSelected(brand._id)}
                    >
                      {brand.websiteUrl && (
                        <div className="brand-detail-item">
                          <img src={WebsiteIcon} alt="Website" className="brand-icon" />
                          {getHostname(brand.websiteUrl)}
                        </div>
                      )}
                      {brand.industry && (
                        <div className="brand-detail-item">
                          <img src={IndustryIcon} alt="Industry" className="brand-icon" />
                          {brand.industry}
                        </div>
                      )}
                      {brand.tone && (
                        <div className="brand-detail-item">
                          <img src={ToneIcon} alt="Tone" className="brand-icon" />
                          {brand.tone}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )
            }
          </TabPanel>

          <TabPanel>
            <form className="brand-details-popup" onSubmit={formik.handleSubmit}>
              <Input
                label="Website"
                placeholder="https://example.com"
                name="websiteUrl"
                type="url"
                formik={formik}
              />

              <Input
                label="Industry"
                placeholder="Startup tech marketing"
                name="industry"
                type="text"
                formik={formik}
              />

              <Input
                label="Tone"
                placeholder="Friendly, professional, casual"
                name="tone"
                type="text"
                formik={formik}
              />

              <button
                className="primary"
                type="submit"
                disabled={
                  (!formik.values.industry && !formik.values.tone && !formik.values.websiteUrl) ||
                  !formik.isValid
                }
              >
                Add & Regenerate
              </button>
            </form>
          </TabPanel>
        </Tabs>
      </div>
    </Popup>
  )
}

